import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import colors from '../theme/colors';
import { IconHeadphones } from './icons';

const SOUNDCLOUD_PLAYLIST_URL = 'https://soundcloud.com/fuckinsomniacom/sets/fuckinsomnia';
const SOUNDCLOUD_EMBED_SRC =
  `https://w.soundcloud.com/player/?url=${encodeURIComponent(SOUNDCLOUD_PLAYLIST_URL)}` +
  '&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false' +
  '&show_user=false&show_reposts=false&show_teaser=false&visual=false';

// Widget layout (measured): artwork + waveform ~165px, SoundCloud footer
// ~45px, then ~28px per tracklist row. Below ~240px total the widget hides
// the tracklist entirely; above the cap it scrolls the tracklist internally.
const WIDGET_CHROME_HEIGHT = 220;
const WIDGET_TRACK_ROW_HEIGHT = 30;
const MIN_WIDGET_HEIGHT = 240;

// Loads the SoundCloud Widget API script once and resolves with window.SC.
let widgetApiPromise = null;
const loadWidgetApi = () => {
  if (window.SC && window.SC.Widget) return Promise.resolve(window.SC);
  if (!widgetApiPromise) {
    widgetApiPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://w.soundcloud.com/player/api.js';
      script.onload = () => resolve(window.SC);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return widgetApiPromise;
};

const MusicContainer = styled.div`
  position: fixed;
  top: 30px;
  right: 68px;
  z-index: 15000;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 70px;
    right: 68px;
    z-index: 10000;
  }
`;

const MusicButton = styled.button`
  background: none;
  border: none;
  color: ${colors.accent.primary};
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  padding: 0;
  margin: 0;
  outline: none;

  &:hover {
    opacity: 0.8;
    transform: scale(1.1);
  }

  &:active {
    opacity: 0.6;
    transform: scale(0.95);
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    width: 29px;
    height: 29px;
  }
`;

const MusicLabel = styled.div`
  margin-top: 2px;
  color: ${colors.accent.primary};
  font-size: 8px;
  font-weight: 600;
  text-shadow: 0 1px 5px rgba(45, 27, 61, 0.6);
  line-height: 1;
  text-align: center;
`;

const PlayerPanel = styled(motion.div)`
  position: fixed;
  background: ${colors.base.themeTertiary};
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  padding: 6px;
  width: 600px;
  z-index: 15000;

  @media (max-width: 768px) {
    width: min(92vw, 460px);
  }
`;

const MusicPlayer = () => {
  const [showPlayer, setShowPlayer] = useState(false);
  const [musicPos, setMusicPos] = useState(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const [trackCount, setTrackCount] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const iframeRef = useRef(null);

  // Ask the SoundCloud Widget API how many tracks the playlist holds so the
  // iframe can grow with the tracklist (capped at half the viewport below).
  useEffect(() => {
    let cancelled = false;
    loadWidgetApi()
      .then((SC) => {
        if (cancelled || !iframeRef.current) return;
        const widget = SC.Widget(iframeRef.current);
        widget.bind(SC.Widget.Events.READY, () => {
          widget.getSounds((sounds) => {
            if (!cancelled && Array.isArray(sounds)) setTrackCount(sounds.length);
          });
        });
      })
      .catch(() => {}); // script blocked: keep the fallback height
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const widgetHeight = Math.max(
    MIN_WIDGET_HEIGHT,
    Math.min(
      WIDGET_CHROME_HEIGHT + trackCount * WIDGET_TRACK_ROW_HEIGHT,
      Math.round(viewportHeight / 2)
    )
  );

  // Position dynamique du bouton
  useEffect(() => {
    const updatePosition = () => {
      const minDimension = Math.min(window.innerWidth, window.innerHeight);
      const isMobile = minDimension < 769;
      const isLandscape = window.innerHeight < window.innerWidth;
      const isMobileLandscape = isMobile && isLandscape;

      if (isMobile && !isMobileLandscape) {
        setMusicPos(null);
        return;
      }

      const profile = document.getElementById('user-profile');
      const loginContainer = document.getElementById('auth-login-container');
      const targetElement = profile || loginContainer;

      if (!targetElement) return;

      const rect = targetElement.getBoundingClientRect();
      const buttonSize = 36;
      const premiumButtonWidth = 36;
      const spacingBetweenButtons = 10;
      const spacingFromProfile = 10;

      const top = Math.round(rect.top + window.scrollY + (rect.height - buttonSize) / 2) - 6;
      var left = Math.round(rect.left + window.scrollX - spacingFromProfile - premiumButtonWidth - spacingBetweenButtons - buttonSize);
      left = (profile === null || profile === undefined) ? (left + premiumButtonWidth + spacingBetweenButtons) : left;

      setMusicPos({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, { passive: true });
    const interval = setInterval(updatePosition, 500);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      clearInterval(interval);
    };
  }, []);

  // Position du panneau du lecteur quand il s'ouvre
  useEffect(() => {
    if (showPlayer && buttonRef.current) {
      const updatePanelPosition = () => {
        const rect = buttonRef.current.getBoundingClientRect();
        const isMobile = Math.min(window.innerWidth, window.innerHeight) < 769;
        const panelWidth = isMobile ? Math.min(window.innerWidth * 0.92, 460) : 600;
        const margin = 10;

        let left = isMobile ? rect.left : rect.right - panelWidth;
        if (left < margin) left = rect.left;
        if (left + panelWidth > window.innerWidth - margin) left = window.innerWidth - panelWidth - margin;
        left = Math.max(margin, left);

        setPanelPos({ top: rect.bottom + 3, left });
      };

      updatePanelPosition();
      window.addEventListener('resize', updatePanelPosition);
      window.addEventListener('scroll', updatePanelPosition, { passive: true });

      return () => {
        window.removeEventListener('resize', updatePanelPosition);
        window.removeEventListener('scroll', updatePanelPosition);
      };
    }
  }, [showPlayer]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowPlayer(false);
      }
    };

    if (showPlayer) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPlayer]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showPlayer) {
        setShowPlayer(false);
      }
    };

    if (showPlayer) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPlayer]);

  return (
    <>
      <MusicContainer style={musicPos ? { top: musicPos.top, left: musicPos.left, right: 'auto' } : undefined}>
        <MusicButton
          ref={buttonRef}
          onClick={() => setShowPlayer(!showPlayer)}
          title="Lecteur de musique"
        >
          <IconHeadphones size={24} />
        </MusicButton>
        <MusicLabel>Music</MusicLabel>
      </MusicContainer>

      {createPortal(
        // Always mounted (never conditionally rendered) so the SoundCloud iframe
        // never reloads/stops playback when the panel is closed — just hidden.
        <PlayerPanel
          ref={panelRef}
          style={{
            ...panelPos,
            pointerEvents: showPlayer ? 'auto' : 'none',
          }}
          initial={false}
          animate={{ opacity: showPlayer ? 1 : 0, y: showPlayer ? 0 : -10 }}
          transition={{ duration: 0.2 }}
        >
          <iframe
            // The widget only lays out its tracklist at load time, so reload it
            // once the real height is known (before any playback can start).
            key={trackCount > 0 ? 'sized' : 'initial'}
            ref={iframeRef}
            title="SoundCloud player"
            width="100%"
            height={widgetHeight}
            scrolling="no"
            frameBorder="no"
            allow="autoplay"
            src={SOUNDCLOUD_EMBED_SRC}
            style={{ display: 'block', transition: 'height 0.2s ease' }}
          />
        </PlayerPanel>,
        document.body
      )}
    </>
  );
};

export default MusicPlayer;
